/**
 * <s-welcome-popup> — opens its <dialog> once per browsing session after a short delay.
 *
 * Attributes:
 *   storage-key — sessionStorage key; once closed it stays closed until the next visit (new session).
 *   ends-at     — ISO date; after this moment the popup never opens.
 *   delay       — milliseconds before opening (default 1200).
 *
 * A button with [data-apply-code] adds its data-code discount to the cart and shows its data-applied label.
 */
class SWelcomePopup extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const key = this.getAttribute('storage-key') || 'kjh-welcome-popup';
    const endsAt = Date.parse(this.getAttribute('ends-at') || '');

    dialog.addEventListener('close', () => {
      try { sessionStorage.setItem(key, '1'); } catch (e) { /* storage unavailable */ }
    }, opts);

    const applyButton = this.querySelector('[data-apply-code]');
    if (applyButton) {
      applyButton.addEventListener('click', async () => {
        const code = (applyButton.dataset.code || '').trim();
        if (!code || applyButton.getAttribute('aria-busy') === 'true') return;
        applyButton.setAttribute('aria-busy', 'true');
        let applied = false;
        try {
          const result = await window.Shopify?.actions?.updateCart?.({ discountCodes: [code] }, { event: { context: 'cart' } });
          applied = !!result && !result.userErrors?.length;
        } catch (e) { applied = false; }
        if (!applied) {
          try {
            const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ discount: code }),
            });
            applied = res.ok;
          } catch (e) { applied = false; }
        }
        // Also store the code in the session so checkout applies it even if the cart was empty.
        try { await fetch(`${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }); } catch (e) { /* ignore */ }
        applyButton.removeAttribute('aria-busy');
        const label = applyButton.querySelector('[data-apply-label]');
        if (label && applyButton.dataset.applied) label.textContent = applyButton.dataset.applied;
      }, opts);
    }

    if (!Number.isNaN(endsAt) && Date.now() > endsAt) return;
    let seen = false;
    try { seen = sessionStorage.getItem(key) === '1'; } catch (e) { seen = false; }
    if (seen) return;

    const delay = Number(this.getAttribute('delay') || 1200);
    this.#timer = setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog:modal')) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }
}

if (!customElements.get('s-welcome-popup')) customElements.define('s-welcome-popup', SWelcomePopup);
